'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useLanguage } from '../context/LanguageContext';
import { useConsent } from '@/context/ConsentContext';

import { EditableContent } from './EditableContent';

const footerLinks = {
  solutions: [
    { nameKey: 'footer.link.smartpayment', href: '#' },
    { nameKey: 'footer.link.ecommerce', href: '#' },
    { nameKey: 'footer.link.pos', href: '#' },
    { nameKey: 'footer.link.gateway', href: '#' },
    { nameKey: 'footer.link.fraud', href: '#' },
  ],
  products: [
    { nameKey: 'footer.link.processing', href: '#' },
    { nameKey: 'footer.link.mobile', href: '#' },
    { nameKey: 'footer.link.invoicing', href: '#' },
    { nameKey: 'footer.link.terminal', href: '#' },
    { nameKey: 'footer.link.apis', href: '#' },
  ],
  company: [
    { nameKey: 'footer.link.about', href: '#' },
    { nameKey: 'footer.link.careers', href: '#' },
    { nameKey: 'footer.link.press', href: '#' },
    { nameKey: 'footer.link.partners', href: '#' },
    { nameKey: 'footer.link.blog', href: '#' },
  ],
  support: [
    { nameKey: 'footer.link.help', href: '#' },
    { nameKey: 'footer.link.docs', href: '#' },
    { nameKey: 'footer.link.api_ref', href: '#' },
    { nameKey: 'footer.link.status', href: '#' },
    { nameKey: 'footer.link.security', href: '#' },
  ],
  legal: [
    { nameKey: 'footer.link.privacy', href: '#' },
    { nameKey: 'footer.link.terms', href: '#' },
    { nameKey: 'footer.link.cookies', href: '#' },
    { nameKey: 'footer.link.compliance', href: '#' },
  ],
};

const socialLinks = [
  { name: 'Twitter', href: '#', icon: '/images/prime_twitter.svg' },
  { name: 'LinkedIn', href: '#', icon: '/images/devicon_linkedin.svg' },
  { name: 'Facebook', href: '#', icon: '/images/devicon_facebook.svg' },
  { name: 'Instagram', href: '#', icon: '/images/skill-icons_instagram.svg' },
  { name: 'GitHub', href: '#', icon: '/images/simple-icons_github.svg' },
];

export default function Footer() {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const { t, isRTL } = useLanguage();
  const { openPreferences } = useConsent();

  return (
    <footer className="bg-space-black border-t border-white/5 relative overflow-hidden">
      {/* Ambient Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-neon-red/50 to-transparent" />

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 relative z-10">
        {/* Newsletter Section */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl bg-white/5 border border-white/10 p-6 sm:p-10 backdrop-blur-sm"
          >
            <h3 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              <EditableContent id="footer.title" showEditIcon={true} />
            </h3>
            <p className="mt-2 text-lg text-gray-400">
              <EditableContent id="footer.description" showEditIcon={true} />
            </p>
            <form className="mt-6 flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                required
                className="min-w-0 flex-auto rounded-xl border border-white/10 px-4 py-3 text-white
                         shadow-sm placeholder:text-gray-500 focus:ring-2 focus:ring-neon-red
                         bg-black/20 backdrop-blur-md"
                placeholder={t('footer.newsletter.placeholder')}
              />
              <button
                type="submit"
                className="flex-none rounded-xl bg-neon-red px-6 py-3 text-sm font-bold text-white 
                         shadow-[0_0_20px_rgba(255,46,77,0.3)] hover:bg-neon-red/80 hover:shadow-[0_0_30px_rgba(255,46,77,0.5)]
                         transition-all duration-200"
              >
                <EditableContent id="footer.newsletter.subscribe" showEditIcon={true} />
              </button>
            </form>
          </motion.div>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-5">
          {/* Solutions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold text-white">
              <EditableContent id="footer.resources" showEditIcon={true} />
            </h4>
            <ul className="mt-4 space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.nameKey}>
                  <Link
                    href={link.href}
                    className="group text-gray-400 hover:text-neon-red
                             transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span className="hover:translate-x-1 transition-transform duration-200">
                      <EditableContent id={link.nameKey} showEditIcon={true} />
                    </span>
                    <svg
                      className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d={
                          isRTL
                            ? 'M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                            : 'M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        }
                        clipRule="evenodd"
                      />
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Products */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold text-white">
              <EditableContent id="footer.products" showEditIcon={true} />
            </h4>
            <ul className="mt-4 space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.nameKey}>
                  <Link
                    href={link.href}
                    className="group text-gray-400 hover:text-neon-red
                             transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span className="hover:translate-x-1 transition-transform duration-200">
                      <EditableContent id={link.nameKey} showEditIcon={true} />
                    </span>
                    <svg
                      className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d={
                          isRTL
                            ? 'M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                            : 'M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        }
                        clipRule="evenodd"
                      />
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="text-lg font-semibold text-white">
              <EditableContent id="footer.company" showEditIcon={true} />
            </h4>
            <ul className="mt-4 space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.nameKey}>
                  <Link
                    href={link.href}
                    className="group text-gray-400 hover:text-neon-red
                             transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span className="hover:translate-x-1 transition-transform duration-200">
                      <EditableContent id={link.nameKey} showEditIcon={true} />
                    </span>
                    <svg
                      className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d={
                          isRTL
                            ? 'M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                            : 'M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        }
                        clipRule="evenodd"
                      />
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Support */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h4 className="text-lg font-semibold text-white">
              <EditableContent id="footer.support" showEditIcon={true} />
            </h4>
            <ul className="mt-4 space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.nameKey}>
                  <Link
                    href={link.href}
                    className="group text-gray-400 hover:text-neon-red
                             transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span className="hover:translate-x-1 transition-transform duration-200">
                      <EditableContent id={link.nameKey} showEditIcon={true} />
                    </span>
                    <svg
                      className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d={
                          isRTL
                            ? 'M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                            : 'M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        }
                        clipRule="evenodd"
                      />
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <h4 className="text-lg font-semibold text-white">
              <EditableContent id="footer.legal" showEditIcon={true} />
            </h4>
            <ul className="mt-4 space-y-3">
              <li>
                <button
                  type="button"
                  onClick={openPreferences}
                  className="group text-gray-400 hover:text-neon-red
                           transition-colors duration-200 flex items-center space-x-2"
                >
                  <span className="hover:translate-x-1 transition-transform duration-200">
                    <EditableContent id="footer.link.cookies" showEditIcon={true} />
                  </span>
                </button>
              </li>
              {footerLinks.legal.filter((l) => l.nameKey !== 'footer.link.cookies').map((link) => (
                <li key={link.nameKey}>
                  <Link
                    href={link.href}
                    className="group text-gray-400 hover:text-neon-red
                             transition-colors duration-200 flex items-center space-x-2"
                  >
                    <span className="hover:translate-x-1 transition-transform duration-200">
                      <EditableContent id={link.nameKey} showEditIcon={true} />
                    </span>
                    <svg
                      className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d={
                          isRTL
                            ? 'M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                            : 'M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        }
                        clipRule="evenodd"
                      />
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Social Links */}
        <div className={`mt-12 flex justify-center ${isRTL ? 'space-x-reverse space-x-6' : 'space-x-6'}`}>
          {socialLinks.map(({ name, href, icon }) => (
            <a
              key={name}
              href={href}
              aria-label={name}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-neon-red transition-colors duration-200"
            >
              <img
                src={icon}
                alt={name}
                className={`w-6 h-6 transition-all duration-300 hover:scale-110 invert opacity-70 hover:opacity-100`}
              />
            </a>
          ))}
        </div>

        {/* Copyright */}
        <p className="mt-8 text-center text-xs text-gray-500">
          &copy; {currentYear} <EditableContent id="footer.companyName" showEditIcon={true} />
        </p>
      </div>
    </footer>
  );
}
